import { createContext, useCallback, useContext, useEffect, useState } from 'react'

import Immutable from 'immutable'
import { useSelector } from 'react-redux'

import { MenuApiRecipeResponse } from '@library/menu-service/queries/menuApiRecipes/types'

import { normaliseData } from 'apis/transformers/normaliseData'
import { formatIngredients } from 'apis/transformers/recipes/recipeHelpers'
import { getNumPortions } from 'selectors/basket'

import { useBrandInfo } from '../domains/brand'
import { findTag } from '../selectors/recipe'

type Recipe = Immutable.Map<string, unknown>

/**
 *
 * A context to hold a single `Recipe` object
 *
 * To be used in e.g. RecipeTile and RecipeDetails
 *
 */
const RecipeContext = createContext<Recipe>(null as unknown as Recipe)
RecipeContext.displayName = 'RecipeContext'

export const RecipeContextProvider = RecipeContext.Provider
export const useRecipe = (): Recipe => useContext(RecipeContext)
export const useRecipeField = <TField = unknown>(
  fieldSelector: string | string[],
  notSetValue?: unknown,
): TField => {
  const recipe = useRecipe()

  if (!recipe) {
    return notSetValue as TField
  }

  if (Array.isArray(fieldSelector)) {
    return recipe.getIn(fieldSelector, notSetValue)
  }

  return recipe.get(fieldSelector, notSetValue) as TField
}

export const useRecipeTitle = (): string => useRecipeField<string>('title')
export const useRecipeId = (): string | null => useRecipeField<string | null>('id', null)

export const useRecipeCookingTime = (): number => {
  const recipe = useRecipe()
  const numPortions = useSelector(getNumPortions)
  const cookingTime = recipe.getIn(['prepTimes', `for${numPortions || 2}`])

  return cookingTime as number
}

type BrandTag = {
  slug: string
  text: string
  theme: {
    name: string
    color: string
    borderColor: string
  }
}

export const useRecipeBrandTagline = (): string | null => {
  const recipe = useRecipe()

  if (!recipe) {
    return null
  }

  return recipe.get('tagline') as string
}

export const useRecipeBrandAvailabilityTagline = (): string | null => {
  const recipe = useRecipe()

  if (!recipe) {
    return null
  }

  const availability = recipe.get('availability', null) as string | null

  // Return new-eme when recipe has isNew to true and
  // when we do not have any availability in meta
  if (!availability && recipe.get('isNew')) {
    return 'new-eme'
  }

  return availability
}

const useTag = (tagline: string | null): BrandTag | null => {
  const brandInfo = useBrandInfo()
  const brandTags = brandInfo.brand?.tags || []

  if (!tagline || !brandTags.length) {
    return null
  }

  return findTag(brandTags, tagline)
}

export const useRecipeBrandTag = (): BrandTag | null => {
  const tagline = useRecipeBrandTagline()

  return useTag(tagline)
}

export const useRecipeBrandAvailabilityTag = (): BrandTag | null => {
  const tagline = useRecipeBrandAvailabilityTagline()

  return useTag(tagline)
}

export const useRecipeIsFineDineIn = (): boolean => {
  const recipe = useRecipe()

  if (!recipe) {
    return false
  }

  return recipe.get('isFineDineIn', false) as boolean
}

const nutritionalValues = {
  per_100g: {
    fibre_mg: 1000,
    protein_mg: 1000,
    net_weight_mg: 1000,
    energy_kj: 1000,
    carbs_sugars_mg: 1000,
    fat_saturates_mg: 1000,
    carbs_mg: 1000,
    salt_mg: 1000,
    fat_mg: 1000,
    energy_kcal: 1000,
  },
  per_portion: {
    fibre_mg: 1000,
    protein_mg: 1000,
    net_weight_mg: 1000,
    energy_kj: 1000,
    carbs_sugars_mg: 1000,
    fat_saturates_mg: 1000,
    carbs_mg: 1000,
    salt_mg: 1000,
    fat_mg: 1000,
    energy_kcal: 1000,
  },
}

export const useFetchRecipe = (
  fetchRecipe: () => Promise<MenuApiRecipeResponse>,
): {
  error: boolean
  loading: boolean
  recipe: Immutable.Map<string, any>
  reload: () => Promise<void>
} => {
  const [error, setError] = useState(false)
  const [loading, setLoading] = useState(true)
  const [recipe, setRecipe] = useState(
    Immutable.fromJS({
      id: '1',
      type: 'recipe',
      attributes: {
        nutritional_information: {
          for1: nutritionalValues,
          for2: nutritionalValues,
          for3: nutritionalValues,
          for4: nutritionalValues,
          for5: nutritionalValues,
        },
      },
      relationships: { ingredients: { data: [...Array(10)].map((_, index) => ({ id: index })) } },
    }),
  )

  const fetchData = useCallback(async () => {
    try {
      const response = await fetchRecipe()
      const normalisedData = normaliseData(response) as unknown as {
        data: {
          attributes: {
            nutritional_information: Record<string, Record<string, number>>
          }
          relationships: {
            ingredients: {
              data: Record<string, unknown>[]
            }
          }
        }[]
        ingredient: Record<string, unknown>
      }
      const rawIngredients = normalisedData.data[0].relationships.ingredients.data
      const formattedIngredients = formatIngredients(rawIngredients, normalisedData.ingredient)
      normalisedData.data[0].relationships.ingredients.data = formattedIngredients
      const retrievedRecipeData = Immutable.fromJS(normalisedData.data[0])

      setRecipe(retrievedRecipeData)
      setError(false)
      setLoading(false)
    } catch {
      setError(true)
      setLoading(false)
    }
  }, [fetchRecipe])

  useEffect(() => {
    fetchData()
  }, [fetchData, fetchRecipe])

  return {
    error,
    loading,
    recipe,
    reload: fetchData,
  }
}

export const getRecipeIngredients = (
  recipe: Immutable.Map<string, any>,
): {
  ingredients: Immutable.List<Immutable.Map<string, any>>
  size: number
} => {
  const ingredients = recipe.getIn(['relationships', 'ingredients', 'data'])
  const convertedIngredients = Immutable.fromJS(ingredients)

  return {
    ingredients: convertedIngredients,
    size: convertedIngredients.size,
  }
}
